import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { 
    collection, query, where, onSnapshot, addDoc, 
    serverTimestamp, orderBy, updateDoc, doc, deleteDoc 
} from 'firebase/firestore';
import { format, differenceInDays } from 'date-fns';
import { 
    UserCheck, CheckSquare, PlusCircle, Trash2, 
    Droplets, Activity, ClipboardList 
} from 'lucide-react';

const WinnieTracker = ({ user }) => {
    const [tasks, setTasks] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [taskInput, setTaskInput] = useState("");
    const [lastSaved, setLastSaved] = useState(null); // Correctly placed at top
    
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');

    // 1. Thursday Logic: Re-verified
    const isThursday = today.getDay() === 4;

    const healthProtocols = [
        { label: "Baking Soda + Hot Water (AM)", type: "Daily" },
        { label: "Rehmannia Glutinosa Dose", type: "Daily" },
        { label: "Blood Sugar Points Check", type: "Health" },
        ...(isThursday ? [{ label: "FOOD PAUSE (Thursday)", type: "Protocol" }] : [])
    ];

    useEffect(() => {
        if (!user) return;
        const q = query(
            collection(db, 'personal_chores'), 
            where('userId', '==', user.uid), 
            orderBy('timestamp', 'desc')
        );
        return onSnapshot(q, (snapshot) => {
            setTasks(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    }, [user]);

    // 2. SAVING ACTION: Restored to top-level logic
    const handleProtocolToggle = async (label) => {
        const existingTask = tasks.find(t => t.label === label && t.date === todayStr);

        try {
            if (existingTask) {
                await updateDoc(doc(db, 'personal_chores', existingTask.id), {
                    status: existingTask.status === 'completed' ? 'pending' : 'completed',
                    completedAt: serverTimestamp()
                });
            } else {
                await addDoc(collection(db, 'personal_chores'), {
                    userId: user.uid,
                    label: label,
                    status: 'completed',
                    date: todayStr,
                    timestamp: serverTimestamp(),
                    group: 'health_protocol'
                });
            }
            // TRIGGER VISUAL PROOF
            setLastSaved(label);
            setTimeout(() => setLastSaved(null), 1000); 
        } catch (err) {
            console.error("Save failed:", err);
        }
    };

    const addManualTask = async () => {
        if (!taskInput) return;
        await addDoc(collection(db, 'personal_chores'), {
            userId: user.uid,
            label: taskInput,
            status: 'pending',
            date: todayStr,
            timestamp: serverTimestamp(),
            group: 'manual_chore'
        });
        setTaskInput("");
        setIsAdding(false);
    };

    const getWinnieStatus = (date) => {
        const lastPeriod = new Date(2026, 1, 26); // Base date
        const cycleDay = (differenceInDays(date, lastPeriod) % 28) + 1;
        
        if (cycleDay >= 1 && cycleDay <= 7) return { status: "Menstruation", mode: "SAFE", color: "text-green-400" };
        if (cycleDay >= 11 && cycleDay <= 17) return { status: "CONCEPTIONAL", mode: "MAX FERTILITY", color: "text-pink-500 font-black" };
        return { status: "Luteal Phase", mode: "LESS CAPABLE", color: "text-blue-400" };
    };

    const winnie = getWinnieStatus(today);

    return (
        <div className="max-w-md mx-auto bg-[#0f172a] min-h-screen text-slate-100 p-6 font-serif">
            <header className="mb-6 border-b border-pink-500/40 pb-4">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-black text-pink-500 italic uppercase">Winnie Audit</h1>
                        <p className={`text-xs font-black mt-1 uppercase tracking-widest ${winnie.color}`}>
                            {winnie.status} — {winnie.mode}
                        </p>
                    </div>
                    <div className="bg-pink-500/10 p-3 rounded-xl border border-pink-500/30 text-right shadow-[0_0_15px_rgba(236,72,153,0.1)]">
                        <p className="text-[8px] text-pink-300 uppercase tracking-widest font-bold">Cycle Day</p>
                        <p className="text-2xl font-black text-white">{(differenceInDays(today, new Date(2026, 1, 26)) % 28) + 1}</p>
                    </div>
                </div>
            </header>

            {isThursday && (
                <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white p-4 rounded-2xl mb-6 animate-pulse border-2 border-white/50 shadow-lg flex items-center gap-4">
                    <Activity size={24} />
                    <div className="flex-1">
                        <p className="font-black uppercase text-sm tracking-tighter">Protocol: Food Pause</p>
                        <p className="text-[10px] font-bold opacity-90">Hot water + Baking Soda ONLY.</p>
                    </div>
                </div>
            )}

            <section className="mb-8 space-y-4">
                <h3 className="text-[10px] font-black uppercase text-pink-500/70 flex items-center gap-2 tracking-widest">
                    <Droplets size={12}/> Vital Health Protocols
                </h3>
                {healthProtocols.map((h, i) => {
                    const task = tasks.find(t => t.label === h.label && t.date === todayStr);
                    const isDone = task?.status === 'completed';
                    const isJustSaved = lastSaved === h.label;

                    return (
                        <div 
                            key={i} 
                            onClick={() => handleProtocolToggle(h.label)}
                            className={`p-5 rounded-2xl border cursor-pointer transition-all duration-300 flex justify-between items-center group
                            ${isDone ? 'bg-pink-600/20 border-pink-500 shadow-[0_0_20px_rgba(236,72,153,0.2)]' : 'bg-slate-900/50 border-white/10'}
                            ${isJustSaved ? 'scale-105 border-green-400 shadow-[0_0_25px_rgba(74,222,128,0.4)]' : ''}`}
                        >
                            <span className={`text-sm font-bold uppercase tracking-tight ${isDone ? 'text-white' : 'text-slate-400'}`}>
                                {h.label} {isJustSaved && "✓ SAVED"}
                            </span>
                            <div className={`${isDone ? 'text-pink-500' : 'text-slate-700 group-hover:text-pink-300'} ${isJustSaved ? 'text-green-400 animate-bounce' : ''}`}>
                                <CheckSquare size={32} fill={isDone ? "currentColor" : "none"} />
                            </div>
                        </div>
                    );
                })}
            </section>

            <section className="space-y-4">
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <h3 className="text-[10px] font-black uppercase text-slate-500 flex items-center gap-2 tracking-widest">
                        <ClipboardList size={12}/> Daily Chores & Client Log
                    </h3>
                    <button onClick={() => setIsAdding(!isAdding)} className="text-pink-500 hover:scale-125 transition-all">
                        <PlusCircle size={20} />
                    </button>
                </div>

                {isAdding && (
                    <div className="flex gap-2 mb-4 bg-white/5 p-2 rounded-xl border border-white/10">
                        <input 
                            className="bg-transparent text-xs flex-1 outline-none px-2 text-white" 
                            placeholder="Add task..." 
                            value={taskInput} 
                            onChange={(e) => setTaskInput(e.target.value)} 
                            onKeyPress={(e) => e.key === 'Enter' && addManualTask()}
                        />
                        <button onClick={addManualTask} className="bg-pink-600 px-4 py-2 rounded-lg text-[10px] font-black uppercase">Save</button>
                    </div>
                )}

                <div className="space-y-3">
                    {tasks.filter(t => t.group === 'manual_chore').map(task => (
                        <div key={task.id} className={`p-4 rounded-xl border flex justify-between items-center ${task.status === 'completed' ? 'bg-white/5 border-white/5 opacity-50' : 'bg-slate-900 border-white/10'}`}>
                            <span className={`text-sm ${task.status === 'completed' ? 'line-through text-slate-500' : 'text-white font-medium'}`}>{task.label}</span>
                            <div className="flex gap-3">
                                <button onClick={() => deleteDoc(doc(db, 'personal_chores', task.id))} className="text-slate-700 hover:text-red-500"><Trash2 size={16}/></button>
                                <button 
                                    onClick={() => updateDoc(doc(db, 'personal_chores', task.id), { status: task.status === 'completed' ? 'pending' : 'completed', completedAt: serverTimestamp() })} 
                                    className={task.status === 'completed' ? 'text-green-500' : 'text-pink-500'}
                                >
                                    <CheckSquare size={22}/>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default WinnieTracker;